import { CommonModule } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Component, OnInit, ElementRef, HostListener, ViewChild, AfterViewInit } from '@angular/core'
import { AbstractControl, FormBuilder, FormControl, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { ResearchService } from '../../services/research.service'
import { Advanced, AdvancedSearchMultiOption, AdvancedSearchOption, QuestionAndAnswer } from '../../types'
import { ConfigService } from '../../services/config.service'
import { TranslocoModule } from '@jsverse/transloco'
import { StatisticsService } from '../../services/statistics.service'
import { SearchDropdownComponent } from '../search-dropdown/search-dropdown.component'
import { SearchSelectbuttonComponent } from '../search-selectbutton/search-selectbutton.component'
import { AutoFocusDirective } from '../../directives/autofocus.directive'

interface SearchForm {
   input: FormControl<string | null>
   fromyear: FormControl<string | null>
   toyear: FormControl<string | null>
   contentType:AdvancedSearchMultiOption | null
   date: AdvancedSearchMultiOption | null
   online: AdvancedSearchOption | null
   regenerate: boolean
   // TODO maybe we should put the '.detect' stuff here too
}


@Component({
   selector: 'app-searchbox',
   standalone: true,
   imports: [CommonModule, ReactiveFormsModule, AutoFocusDirective, TranslocoModule, SearchDropdownComponent, SearchSelectbuttonComponent],
   templateUrl: './searchbox.component.html'
})
export class SearchboxComponent implements OnInit, AfterViewInit {

   @ViewChild('searchbox') searchBox!: ElementRef

   @HostListener('window:keyup', ['$event'])
   keyEvent(event: KeyboardEvent) {
      if (event.key === 'F8' && event.shiftKey) {
         this.config.currentConfig.streamAnswer = !this.config.currentConfig.streamAnswer
         console.log(`Stream answer testing is ${ this.config.currentConfig.streamAnswer ? 'enabled' : 'disabled'} for now)`)
      }
      if (event.key === 'F9' && event.shiftKey) {
         this.config.setDebug(!this.config.debug)
         if (this.config.debug) {
            this.helpText = this.config.getVersion()
         }
      }
      if (event.key === '/' ) {
         this.searchBox.nativeElement.focus()
      }
   }

   streamAnswer = false
   inDebugMode = false

   /**
    * the below labels are for the transloco extractor to find the keys
    * t(dropdown.contenttype.alltypes, dropdown.contenttype.peer-reviewed, dropdown.contenttype.books, dropdown.contenttype.articles)
   */
   contentTypeOptions: AdvancedSearchMultiOption = { defaultValue: 'alltypes', translationPrefix: 'dropdown.contenttype', selected: null, options: [{value: 'alltypes'}, {value: 'articles'}, {value: 'peer-reviewed'}, {value:'books'}]}
   /**
    * the below labels are for the transloco extractor to find the keys
    * t(dropdown.date.alldates, dropdown.date.last12months, dropdown.date.last5years, dropdown.date.last10years, dropdown.date.custom)
   */
   dateOptions:AdvancedSearchMultiOption = {  defaultValue: 'alldates', translationPrefix: 'dropdown.date', selected: null, options: [{value: 'alldates',  selected: null}, { value: 'last12months', selected: null }, { value: 'last5years'}, {value: 'last10years'}, {value:'custom'}] }
   /**
    * the below labels are for the transloco extractor to find the keys
    * t(select.search.online)
   */
   onlineOption: AdvancedSearchOption = { value: 'online', selected: null, translationPrefix: 'select.search' }

   get hasAnswer(): boolean {
      return (this.research.researchSession?.qandas ?? 0).length > 0
   }

   get remaining() {
      return this.research.user.remaining
   }
   get maxRemaining() {
      return this.research.user.maxRemaining
   }

   get lastQuestion(): QuestionAndAnswer {
      return this.research.qandas[this.research.qandas.length - 1]
   }

   get smallSearchBox() {
      // only for web component so chat box does not overlap
      return !this.config.currentConfig.isStandalone
   }

   get showAdvancedSearch() {
      return this.config.currentConfig.showAdvanced
   }

   get showOnline() {
      return this.config.currentConfig.enableOnline
   }

   initialHelpText = 'searchbox.placeholder.askresearchquestion'
   helpText = this.initialHelpText

   showAdvanced = false
   chatLoading = false
   advancedAplied = 0

   constructor(private research: ResearchService, private formBuilder: FormBuilder,
       private el: ElementRef, private config: ConfigService,
      private stats: StatisticsService) {
      this.research.onLoadingChanged()
         .pipe(takeUntilDestroyed())
         .subscribe((loading) => {
            this.chatLoading = loading
         })

      this.hookupForm()
      this.research.currentQandA$.pipe(takeUntilDestroyed()).subscribe(() => {
         this.updateFormWithCurrentQandA()
         this.updatePlaceholderText()
      })
      this.research.currentQuestion$.pipe(takeUntilDestroyed()).subscribe((q) => {
         this.inputForm.patchValue({ input: q.question, regenerate: q.regenerate })
         this.searchBox?.nativeElement?.focus()
      })
      this.research.onSessionChanged.pipe(takeUntilDestroyed()).subscribe(() => {
         this.updatePlaceholderText()
         // reset the form input
         this.inputForm.patchValue({ input: '', contentType: {...this.contentTypeOptions, }, date: {...this.dateOptions}, online: {...this.onlineOption} })

         // focus the input box
         this.focusIfNeeded()
      })
   }

   // check if the advanced object has a detect value, if set, use it, otherwise check if the control is dirty
   private shouldDetect(controlName: string): boolean{
      let key = controlName + '.detect'
      if (controlName === 'contentType') {
         key = 'type.detect'
      }

      const isDirty = this.inputForm.get(controlName)?.dirty ?? false
      let shouldDetect = true

      // @ts-ignore sorry don't want to fix this TS error, i mean it is undef if incorrect anyway
      const previousDetectValue = this.research.currentQandA()?.answer?.advanced?.[key]
      // if the user touched a control, but then reset it (value === null), it should check if there was a 'detect' value in the API,
      // if that is so, the user wants to explicitly ignore that detected value, so we should set it to false
      if (isDirty && (this.inputForm.get(controlName)?.value === null || this.inputForm.get(controlName)?.value?.selected === null)) {
         shouldDetect = previousDetectValue ?? false
      }
      else if(isDirty) {
         shouldDetect = false
      }
      else if(previousDetectValue === false) {
         shouldDetect = false
      }

      return shouldDetect
   }

   private hookupForm() {
      this.inputForm.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
         this.advancedAplied = 0
         if(value.contentType?.selected) {
            this.advancedAplied++
         }
         if(value.date?.selected) {
            this.advancedAplied++
         }
         if(this.showOnline && value.online?.selected) {
            this.advancedAplied++
         }
         if(value.date?.selected !== 'custom') {
            // reset it back to empty so validation is not triggered (and do not emit event otherwise we loop)
            this.inputForm.controls.fromyear.setValue('', { emitEvent: false })
            this.inputForm.controls.toyear.setValue('', { emitEvent: false })
         }
      })
   }

   showQuotum() {
      return this.remaining >= 0
   }

   /**
   * the below labels are for the transloco extractor to find the keys
    * t(validate.year.past, validate.year.number, validate.year.fromsmallerthanto)
    */
   yearValidator(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         if (!control.value) {
            return null
         }
         this.inputForm.controls.toyear.setErrors(null)
         this.inputForm.controls.fromyear.setErrors(null)

         if (control.value && !control.value.match(/^\d{4}$/)) {
            return { text: 'Year should be a number', label: 'validate.year.number' }
         }
         else if(control.value && (parseInt(control.value) > new Date().getFullYear())) {
            return { text: 'Year should be in the past', label: 'validate.year.past' }
         }
         // check if fromyear is smaller than toyear
         else if (this.inputForm.controls.fromyear.value && this.inputForm.controls.toyear.value) {
            const yearDiff = parseInt(this.inputForm.controls.toyear.value) - parseInt(this.inputForm.controls.fromyear.value)
            if(yearDiff < 0) {
               return { text: 'The \'from\' year must be earlier than the \'to\' year.', label: 'validate.year.fromsmallerthanto' }
            }
         }
         return null
      }
   }

   inputForm = this.formBuilder.group<SearchForm>({
      input: new FormControl<string>('', { updateOn: 'change', validators: [Validators.required]}), //TODO add some minlength validator? Validators.minLength(5)]
      fromyear: new FormControl<string>('', { updateOn: 'blur', validators: [this.yearValidator()]} ),
      toyear: new FormControl<string>('', { updateOn: 'blur', validators: [this.yearValidator()]} ),
      contentType: null,
      date: null,
      online: null,
      regenerate: false
   })

   ngOnInit(): void {
      this.updatePlaceholderText()
      this.inputForm.updateValueAndValidity()
   }

   updateFormWithCurrentQandA() {
      if (this.research.currentQandA()?.answer?.advanced) {
         this.inputForm.patchValue({
            contentType: this.buildContentTypeOptionFromAnswer(this.research.currentQandA()),
            online: this.buildOnlineOptionFromAnswer(this.research.currentQandA()),
            ...this.buildDateOptionFromAnswer(this.research.currentQandA())
         })

         if (this.research.currentQandA()?.answer?.advanced?.['date.value'] || this.research.currentQandA()?.answer?.advanced?.['type.value'] || this.research.currentQandA()?.answer?.advanced?.['online.value']) {
            if(this.research.currentQandA()?.answer?.advanced?.['date.detect'] === false || this.research.currentQandA()?.answer?.advanced?.['type.detect'] === false || (this.showOnline ? this.research.currentQandA()?.answer?.advanced?.['online.detect'] === false : false)) {
               // if any of the detect values is false, this means the user has explicitly set a value, so the advanced does not need to be openend, the user is aware
            }
            else if(this.config.currentConfig.showAdvanced) {
               this.showAdvanced = true
            }
         }
      }
      else {
         this.inputForm.patchValue({ contentType: {...this.contentTypeOptions, }, date: {...this.dateOptions}, online: {...this.onlineOption} })
      }
   }

   ngAfterViewInit() {
      this.focusIfNeeded()
   }

   focusIfNeeded() {
      // only focus when there is no answer in the current session
      if (!this.hasAnswer) {
         this.searchBox?.nativeElement?.focus()
      }
   }

   updatePlaceholderText() {
      if ((this.research.qandas.length ?? 0) === 0) {
         this.helpText = this.initialHelpText
      }
      else {
         this.helpText = 'searchbox.placeholder.askresearchquestionnext'
      }
   }

   getPreviousQuestion() {
      const prevQuestion = this.lastQuestion
      if (prevQuestion) {
         // TODO set correct values to contentType and date
         this.inputForm.setValue({
            input: prevQuestion.question ?? '',
            contentType: this.buildContentTypeOptionFromAnswer(prevQuestion),
            online: this.buildOnlineOptionFromAnswer(prevQuestion),
            regenerate: false,
            ...this.buildDateOptionFromAnswer(prevQuestion)})
      }
   }

   buildContentTypeOptionFromAnswer(answer: QuestionAndAnswer | undefined) {
      if (!answer) {
         return {...this.contentTypeOptions, selected: null}
      }
      return {... this.contentTypeOptions, selected: answer.answer?.advanced?.['type.value'] ?? null}
   }

   buildDateOptionFromAnswer(answer: QuestionAndAnswer| undefined): { date: AdvancedSearchMultiOption, fromyear: string, toyear: string } {
      if (!answer) {
         return {date: {... this.dateOptions, selected: null}, fromyear: '0', toyear: ''}
      }
      // map a yyyy-yyyy value to one of the labels
      const dateValue = answer.answer?.advanced?.['date.value']
      if (dateValue) {
         const label = this.parseDateToLabel(dateValue)
         if (label.match(/^\d{4}-\d{4}$/)) {
            const [fromYear, toYear] = label.split('-')
            return {date: {... this.dateOptions, selected: 'custom'}, fromyear: fromYear, toyear: toYear}
         }
         else {
            return {date: {... this.dateOptions, selected: label}, fromyear: '', toyear: '' }
         }
      }

      return {date: {... this.dateOptions, selected: answer.answer?.advanced?.['date.value'] ?? null}, fromyear: '', toyear: ''}
   }

   buildOnlineOptionFromAnswer(answer: QuestionAndAnswer| undefined) {
      if (!answer) {
         return {... this.onlineOption, selected: null}
      }
      return {... this.onlineOption, selected: answer.answer?.advanced?.['online.value']}
   }

   parseDateToLabel(dateString: string) {
      // there is a yyyy-yyyy date coming in, check if they match last12months, last5years, last10years
      const dateParts = dateString.split('-')
      const fromYear = parseInt(dateParts[0])
      const toYear = parseInt(dateParts[1])
      const currentYear = new Date().getFullYear()
      if (fromYear === currentYear - 1 && toYear === currentYear) {
         return 'last12months'
      }
      else if (fromYear === currentYear - 5 && toYear === currentYear) {
         return 'last5years'
      }
      else if (fromYear === currentYear - 10 && toYear === currentYear) {
         return 'last10years'
      }
      else {
         return dateString
      }
   }

   onSubmit() {
      const query = this.inputForm.value.input
      if (!query) {
         // only do something when there is text in the inputbox
         return
      }
      const advanced = this.buildAdvancedObject()
      // remove the query from the input box since it is open for a new question
      this.inputForm.patchValue({ input: '' })

      this.research.getAnswer(query, advanced, this.inputForm?.value?.regenerate ?? false).then(() => {
         this.updatePlaceholderText()
         // test on one because there is always a dummy first question on an empty session
         if (this.research.qandas.length === 1) {
            this.stats.search(query, 'inputbox_new')
         }
         else {
            this.stats.search(query, 'inputbox_followup')
         }
      })
   }

   buildAdvancedObject() {
      const currentQandA = this.research.currentQandA()

      const previous = currentQandA?.answer?.advanced?.topic ?? currentQandA?.answer?.advanced?.previous ??  currentQandA?.question ?? ''

      const previousDates = this.buildDateOptionFromAnswer(currentQandA)
      const datesSetAndSameAsPrevious = (this.inputForm.value.fromyear !== '' && this.inputForm.value.toyear !== '') ? (this.inputForm.value.fromyear === previousDates.fromyear && this.inputForm.value.toyear === previousDates.toyear) : true
      // update the internal object so it can be send to server when needed (not only in searchbox questions but also more-questions or landing page)
      const advanced: Advanced = {
         previous: previous,
         'type.detect': this.shouldDetect('contentType'),
         'type.value': this.inputForm.value.contentType?.selected ?? null,
         'online.detect': this.shouldDetect('online'),
         'online.value': this.inputForm.value.online?.selected ?? null,
         'date.detect': this.shouldDetect('date') && datesSetAndSameAsPrevious,
         'date.value': this.getDateValue(this.inputForm.value.date?.selected, this.inputForm.value.fromyear, this.inputForm.value.toyear)
      }
      return advanced
   }

   resetForm() {
      // do not reset the input field
      this.inputForm.patchValue({ contentType: {...this.contentTypeOptions}, date: {...this.dateOptions}, online: {...this.onlineOption}, fromyear: '', toyear: '' })

      setTimeout(() => {
         this.el.nativeElement.querySelector('[formcontrolname="contentType"] > select')?.focus()
      }, 50)
   }

   toggleAdvanced() {
      this.showAdvanced = !this.showAdvanced
   }

   skipToSearch() {
      setTimeout(() => {
         this.el.nativeElement.querySelector('#default-search')?.focus()
      }, 50)
   }

   skipToLastQuestion(event: Event) {
      (event.currentTarget as HTMLElement)?.blur()
      const root = this.el.nativeElement.getRootNode()
      if (!root) {
         console.warn('Weird! no root doc?')
         return
      }

      const els = root.querySelectorAll('.answer, .t-error')
      let el = els?.length > 0 ? els[els.length - 1] : null
      if (els.length === 0) {
         el = root.getElementById('chat-container')
         if (!el) {
            el = root.getElementById('landing')
         }
      }
      if (el) {
         el.scrollIntoView({ behavior: 'smooth' });

         (el as HTMLElement)?.focus()
      }
   }

   getDateValue(selected: string | null | undefined, fromyear: string | null | undefined, toyear: string | null | undefined): string | null | undefined {
      // from labels generate a yyyy-yyyy value
      if (selected === 'custom' && fromyear && toyear) {
         return `${fromyear}-${toyear}`
      }
      if(selected && selected === 'alldates') {
         return null
      }
      if(selected && selected === 'last12months') {
         return new Date().getFullYear() - 1 + '-' + new Date().getFullYear()
      }
      if(selected && selected === 'last5years') {
         return new Date().getFullYear() - 5 + '-' + new Date().getFullYear()
      }
      if(selected && selected === 'last10years') {
         return new Date().getFullYear() - 10 + '-' + new Date().getFullYear()
      }
      return null
   }
}
